import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import StatusBadge from "../components/StatusBadge";

export default function Candidates() {
  const [all, setAll] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reqFilter, setReqFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/candidates"), api.get("/requisitions")])
      .then(([c, r]) => {
        setAll(c.data);
        setReqs(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let rows = all;
    if (reqFilter) rows = rows.filter((c) => c.requisition_id === reqFilter);
    if (statusFilter) rows = rows.filter((c) => c.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.full_name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.mobile || "").toLowerCase().includes(q) ||
          (c.skills || "").toLowerCase().includes(q) ||
          (c.current_location || "").toLowerCase().includes(q) ||
          (c.current_company || "").toLowerCase().includes(q) ||
          (c.requisition_title || "").toLowerCase().includes(q),
      );
    }
    if (sort === "recent") rows = [...rows].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (sort === "score") rows = [...rows].sort((a, b) => (b.ai_score?.overall ?? -1) - (a.ai_score?.overall ?? -1));
    if (sort === "name") rows = [...rows].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    if (sort === "experience") rows = [...rows].sort((a, b) => (b.relevant_experience ?? 0) - (a.relevant_experience ?? 0));
    return rows;
  }, [all, search, reqFilter, statusFilter, sort]);

  const statuses = useMemo(() => {
    const s = new Map();
    all.forEach((c) => s.set(c.status, (s.get(c.status) || 0) + 1));
    return Array.from(s.entries()).sort((a, b) => b[1] - a[1]);
  }, [all]);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="candidates-directory-page">
      <div className="flex items-end justify-between border-b border-rule pb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            Directory · {filtered.length} of {all.length}
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Candidates.</h1>
          <p className="text-quiet text-sm mt-3 max-w-2xl">
            Every candidate who has applied — with their full profile, declared skills, the role
            they applied for, compatibility score, and current stage.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mt-6 bg-white border border-rule p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Search</div>
          <input
            data-testid="cand-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, skills, location, company, role…"
            className="mt-1.5 w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white"
          />
        </div>
        <div className="md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Applied role</div>
          <select
            data-testid="cand-req-filter"
            value={reqFilter}
            onChange={(e) => setReqFilter(e.target.value)}
            className="mt-1.5 w-full border border-rule bg-white px-3 py-2 text-sm focus:outline-none focus:border-klein"
          >
            <option value="">All roles</option>
            {reqs.map((r) => (
              <option key={r.id} value={r.id}>
                {`${r.title} · ${r.requisition_code || "—"}`}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Stage</div>
          <select
            data-testid="cand-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1.5 w-full border border-rule bg-white px-3 py-2 text-sm focus:outline-none focus:border-klein"
          >
            <option value="">All stages</option>
            {statuses.map(([s, n]) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")} ({n})
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Sort</div>
          <select
            data-testid="cand-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="mt-1.5 w-full border border-rule bg-white px-3 py-2 text-sm focus:outline-none focus:border-klein"
          >
            <option value="recent">Most recent</option>
            <option value="score">AI score</option>
            <option value="experience">Experience</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </div>
      </div>

      {/* Results */}
      {loading && <div className="mt-6 bg-white border border-rule px-5 py-16 text-center text-quiet text-sm">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="mt-6 bg-white border border-rule px-5 py-16 text-center">
          <div className="font-serif text-2xl text-ink">No candidates match your filters</div>
          <div className="text-sm text-quiet mt-2">
            {all.length === 0
              ? "Share a public apply link to start receiving applications."
              : "Try clearing filters above."}
          </div>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="cand-grid">
          {filtered.map((c) => (
            <CandidateCard key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function Initials({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return (
    <div className="w-12 h-12 border border-ink flex items-center justify-center font-serif text-lg text-ink bg-paper">
      {initials || "?"}
    </div>
  );
}

function CandidateCard({ c }) {
  const skills = (c.skills || "").split(",").map((s) => s.trim()).filter(Boolean);
  const score = c.ai_score?.overall;
  return (
    <Link
      to={`/candidates/${c.id}`}
      data-testid={`cand-card-${c.id}`}
      className="block bg-white border border-rule hover:border-ink hover:-translate-y-[1px] transition-all"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Initials name={c.full_name} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-serif text-2xl text-ink leading-none truncate">{c.full_name}</div>
                <div className="text-xs text-quiet font-mono mt-1 truncate">{c.email}</div>
              </div>
              {score != null && (
                <div className="text-right shrink-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">AI</div>
                  <div className="font-mono text-2xl text-klein leading-none">{score}</div>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <StatusBadge status={c.status} />
              <span className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-ink bg-white truncate max-w-[200px]">
                {c.requisition_title}
              </span>
              {c.requisition_code && (
                <span className="font-mono text-[10px] text-quiet">{c.requisition_code}</span>
              )}
            </div>
          </div>
        </div>

        {/* Grid of details */}
        <div className="mt-5 pt-4 border-t border-rule grid grid-cols-3 gap-3 text-sm">
          <Cell label="Mobile" value={c.mobile} mono />
          <Cell label="Location" value={c.current_location} />
          <Cell label="Notice" value={c.notice_period || "—"} />
          <Cell label="Total exp" value={`${c.total_experience ?? 0} yrs`} mono />
          <Cell label="Relevant" value={`${c.relevant_experience ?? 0} yrs`} mono />
          <Cell label="Company" value={c.current_company || "—"} />
          <Cell label="Current CTC" value={c.current_ctc != null ? `${c.current_ctc} LPA` : "—"} mono />
          <Cell label="Expected CTC" value={c.expected_ctc != null ? `${c.expected_ctc} LPA` : "—"} mono />
          <Cell label="Qualification" value={c.highest_qualification || "—"} />
        </div>

        {/* Skills */}
        {skills.length > 0 && (
          <div className="mt-4 pt-4 border-t border-rule">
            <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">Skills</div>
            <div className="flex flex-wrap gap-1.5">
              {skills.slice(0, 12).map((s) => (
                <span key={s} className="px-2 py-0.5 bg-paper border border-rule text-[11px] font-mono">
                  {s}
                </span>
              ))}
              {skills.length > 12 && (
                <span className="px-2 py-0.5 text-[11px] font-mono text-quiet">+{skills.length - 12}</span>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-rule flex items-center justify-between">
          <div className="font-mono text-[10px] text-quiet">
            Applied {new Date(c.created_at).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-3">
            {c.portfolio_url && (
              <a
                href={c.portfolio_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-mono text-[10px] uppercase tracking-wider text-klein hover:underline"
              >
                Portfolio ↗
              </a>
            )}
            <span className="font-mono text-[10px] uppercase tracking-wider text-quiet">
              View profile →
            </span>
          </div>
        </div>

        {c.cooling_until && new Date(c.cooling_until) > new Date() && (
          <div className="mt-3 px-3 py-2 border border-[#EF4444] text-[10px] font-mono text-[#EF4444] uppercase tracking-wider bg-[#FEF2F2]">
            Cooling period until {c.cooling_until.slice(0, 10)}
          </div>
        )}
      </div>
    </Link>
  );
}

function Cell({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">{label}</div>
      <div className={`mt-0.5 text-ink truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
